import React, { useState } from "react";
import { X, Mail, Lock, User, Loader2 } from "lucide-react";
import { useClientAuth } from "../../context/ClientAuth";

const Field = ({ icon: Icon, ...props }) => (
  <div className="flex items-center gap-3 border border-sand/70 bg-bone rounded-md px-4 py-3 focus-within:border-espresso transition-colors">
    <Icon size={16} className="text-mocha shrink-0" strokeWidth={1.5} />
    <input {...props} className="w-full bg-transparent text-sm text-ink placeholder:text-mocha/70 outline-none" />
  </div>
);

export function ClientAuthDialog({ open, onClose, onSuccess, reason }) {
  const { login, register, startGoogle } = useClientAuth();
  const [mode, setMode] = useState("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const client = mode === "signin"
        ? await login(form.email, form.password)
        : await register({ name: form.name, email: form.email, password: form.password, phone: form.phone || null, terms_accepted: termsAccepted });
      onSuccess?.(client);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="client-auth-dialog">
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-bone rounded-lg shadow-2xl overflow-hidden" role="dialog" aria-modal="true">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 text-mocha hover:text-ink transition-colors" data-testid="client-auth-close" aria-label="Close"><X size={18} /></button>
        <div className="px-7 pt-9 pb-7">
          <p className="text-[10px] tracking-[0.25em] uppercase text-mocha" data-testid="client-auth-eyebrow">FYLLR account</p>
          <h2 className="mt-2 font-serif text-3xl text-ink" data-testid="client-auth-title">{mode === "signin" ? "Welcome back." : "Create your account."}</h2>
          <p className="mt-2 text-sm text-ink/60" data-testid="client-auth-reason">{reason || "Sign in to secure your last-minute booking."}</p>

          <button type="button" onClick={startGoogle} className="mt-6 w-full flex items-center justify-center gap-3 border border-sand rounded-full py-3 text-sm text-ink hover:bg-sand/20 transition-colors" data-testid="client-auth-google">
            <svg width="17" height="17" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </button>

          <div className="my-5 flex items-center gap-4 text-[10px] tracking-[0.2em] uppercase text-mocha">
            <span className="h-px flex-1 bg-sand/60" /> or <span className="h-px flex-1 bg-sand/60" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && <Field icon={User} type="text" required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="client-auth-name" />}
            <Field icon={Mail} type="email" required placeholder="Email address" value={form.email} onChange={set("email")} data-testid="client-auth-email" />
            <Field icon={Lock} type="password" required minLength={8} placeholder={mode === "signup" ? "Create a password (8+ chars)" : "Password"} value={form.password} onChange={set("password")} data-testid="client-auth-password" />
            {mode === "signup" && <Field icon={User} type="tel" placeholder="Mobile (optional)" value={form.phone} onChange={set("phone")} data-testid="client-auth-phone" />}
            {mode === "signup" && <label className="flex items-start gap-3 pt-1 text-xs text-ink/65"><input type="checkbox" required checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="mt-0.5 h-4 w-4 accent-espresso" data-testid="client-auth-terms" /><span>I agree to the FYLLR <a href="/terms" target="_blank" rel="noreferrer" className="underline underline-offset-2">Terms &amp; Conditions</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="underline underline-offset-2">Privacy Policy</a>.</span></label>}
            {error && <p className="text-sm text-burgundy" data-testid="client-auth-error">{error}</p>}
            <button type="submit" disabled={busy || (mode === "signup" && !termsAccepted)} className="w-full flex items-center justify-center gap-2 rounded-full bg-espresso text-bone py-3 text-sm tracking-wide hover:bg-ink disabled:opacity-60 transition-colors" data-testid="client-auth-submit">
              {busy && <Loader2 size={15} className="animate-spin" />}
              {mode === "signin" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-ink/60">
            {mode === "signin" ? "New to FYLLR?" : "Already have an account?"}{" "}
            <button type="button" onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); setError(""); }} className="text-espresso underline underline-offset-2 hover:text-ink" data-testid="client-auth-toggle">
              {mode === "signin" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
